import React, { useState } from 'react';
import { TestSample, SignalData } from '../types';
import { api } from '../services/api';

interface TestSamplesProps {
    onSelect: (result: TestSample, signal: SignalData) => void;
}

const TestSamples: React.FC<TestSamplesProps> = ({ onSelect }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sampleFiles, setSampleFiles] = useState<string[]>([]);
    const [loadingSamples, setLoadingSamples] = useState(false);

    const handleLoadSamples = async () => {
        setLoadingSamples(true);
        setError(null);
        try {
            const data = await api.getSampleFiles();
            setSampleFiles(data.files);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load sample files');
        } finally {
            setLoadingSamples(false);
        }
    };

    const handleSampleFileClick = async (filename: string) => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.predictSample(filename);

            const convertedResult: TestSample = {
                predicted_class: response.predicted_class,
                confidence: response.confidence,
                is_uncertain: response.is_uncertain,
                Normal: response.Normal,
                Supraventricular: response.Supraventricular,
                Ventricular: response.Ventricular,
                Fusion: response.Fusion,
                Unknown: response.Unknown,
                threshold: response.threshold,
                true_label: '',
                true_label_id: 0,
                index: 0,
                is_correct: false,
            };

            const signalData: SignalData = {
                raw: response.signal_raw || [],
                normalized: response.signal_normalized || []
            };

            onSelect(convertedResult, signalData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to predict sample file');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadSample = async (filename: string) => {
        try {
            const blob = await api.downloadSample(filename);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to download sample file');
        }
    };

    const handleLoadTestSample = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await api.getTestSamples(1);
            const sample = data.samples[0];

            const signalData: SignalData = {
                raw: sample.signal_raw || [],
                normalized: sample.signal_normalized || []
            };

            onSelect(sample, signalData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load test data');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            {/* Sample Files Section */}
            <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2 text-center">
                    Sample ECG Files
                </h3>
                <p className="text-sm text-gray-600 mb-4 text-center">
                    Click a sample file to see prediction results or download it
                </p>
                <button
                    onClick={handleLoadSamples}
                    disabled={loadingSamples}
                    className="w-full px-6 py-3 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors text-base"
                >
                    {loadingSamples ? 'Loading Sample Files...' : 'Load Sample Files'}
                </button>

                {sampleFiles.length > 0 && (
                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {sampleFiles.map((file) => (
                            <div key={file} className="flex flex-col gap-1">
                                <button
                                    onClick={() => handleSampleFileClick(file)}
                                    disabled={loading}
                                    className="px-2 py-1.5 bg-blue-50 text-blue-700 font-medium rounded border border-blue-200 hover:bg-blue-100 disabled:opacity-50 transition-colors text-xs truncate"
                                    title={file}
                                >
                                    {file.replace('.csv', '')}
                                </button>
                                <button
                                    onClick={() => handleDownloadSample(file)}
                                    disabled={loading}
                                    className="px-2 py-1.5 bg-green-50 text-green-700 font-medium rounded border border-green-200 hover:bg-green-100 disabled:opacity-50 transition-colors text-xs"
                                    title={`Download ${file}`}
                                >
                                    ⬇ Download
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* MIT-BIH Test Data Section */}
            <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2 text-center">
                    MIT-BIH Test Data
                </h3>
                <p className="text-sm text-gray-600 mb-4 text-center">
                    Click button to load random ECG sample from MIT-BIH Arrhythmia Database
                </p>
                <button
                    onClick={handleLoadTestSample}
                    disabled={loading}
                    className="w-full px-6 py-3 bg-blue-500 text-white font-bold rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors text-base"
                >
                    {loading ? 'Loading...' : 'Load one Test Sample'}
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded font-medium">
                    {error}
                </div>
            )}
        </div>
    );
};

export default TestSamples;
